import React from 'react';
import {
  Checkbox,
  Chip,
  TextField,
  MenuItem,
  Box,
  Autocomplete
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearAllIcon from '@mui/icons-material/ClearAll';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type OptionType = {
  label: string;
  value: string;
  isSelectAll?: boolean;
};

type MultiSelectAutocompleteProps = {
  label: string;
  options: OptionType[];
  selectedValues: string[];
  onChange: (newValues: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  truncateLength?: number;
};

const GenericAutoComplete: React.FC<MultiSelectAutocompleteProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  disabled = false,
  loading = false,
  id,
  truncateLength = 15,
}) => {
  const allOptions = [
    { label: 'Selecionar todos', value: 'select_all', isSelectAll: true },
    ...options.sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.label.localeCompare(b.label);
    }),
  ];

  const truncate = (text: string) =>
    text.length > truncateLength ? `${text.slice(0, truncateLength)}...` : text;

  return (
    <Autocomplete
      fullWidth
      multiple
      disableCloseOnSelect
      disableClearable
      disabled={disabled}
      loading={loading}
      id={id}
      options={allOptions}
      value={options.filter((opt) => selectedValues.includes(opt.value))}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(event, newValue, reason, details) => {
        if (!details?.option) return;
        const { value, isSelectAll } = details.option;

        if (isSelectAll) {
          const allSelected =
            options.length > 0 &&
            options.every((opt) => selectedValues.includes(opt.value));

          const updatedList = allSelected ? [] : options.map((opt) => opt.value);
          onChange(updatedList);
          return;
        }

        const alreadySelected = selectedValues.includes(value);
        const updatedList = alreadySelected
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];

        onChange(updatedList);
      }}
      renderOption={(props, option, { selected }) => {
        if ((option as any).isSelectAll) {
          const allSelected =
            options.length > 0 &&
            options.every((opt) => selectedValues.includes(opt.value));

          return (
            <MenuItem {...props} key="select_all">
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={allSelected}
              />
              <b>{allSelected ? 'Desmarcar todos' : 'Selecionar todos'}</b>
            </MenuItem>
          );
        }

        return (
          <MenuItem {...props} key={option.value}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              style={{ marginRight: 8 }}
            />
            <span>{truncate(option.label)}</span>
          </MenuItem>
        );
      }}
      renderTags={(value, getTagProps) => {
        const displayedTags = value.slice(0, 2);
        return (
          <>
            {displayedTags.map((option, index) => {
              const tagProps = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option.value}
                  label={truncate(option.label)}
                  onDelete={tagProps.onDelete}
                />
              );
            })}
            {value.length > 2 && (
              <Chip
                label={`+${value.length - 2}`}
                sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}
              />
            )}
          </>
        );
      }}
      renderInput={(params) => (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <TextField {...params} label={label} />
          {selectedValues.length > 0 && (
            <ClearAllIcon
              onClick={() => onChange([])}
              sx={{
                position: 'absolute',
                right: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'gray',
                cursor: 'pointer',
                '&:hover': { color: 'black' },
              }}
              titleAccess="Limpar seleção"
            />
          )}
        </Box>
      )}
    />
  );
};

export default GenericAutoComplete;